import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Truck,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  MapPin,
  Calendar,
  XCircle,
  Ban,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent, Badge } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { CancelOrderDialog } from '@components/CancelOrderDialog';
import { formatDateBR } from '@pages/financial/financialUtils';
import { goodsPickupOrderService } from '../../services/goodsPickupOrderService';
import { GoodsPickupOrder, GoodsPickupOrderSearchModel } from '../../types/goodsPickupOrder';
import { PICKUP_ORDER_STATUS_LABELS, pickupOrderStatus, pickupOrderStatusBadge } from './goodsPickupOrderUtils';

export const GoodsPickupOrderListView: React.FC = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState<GoodsPickupOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'delivered' | 'canceled'>('all');
  const [selectedOrder, setSelectedOrder] = useState<GoodsPickupOrder | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<GoodsPickupOrder | null>(null);

  // Stats
  const [totalOrders, setTotalOrders] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const [deliveredOrders, setDeliveredOrders] = useState(0);
  const [canceledOrders, setCanceledOrders] = useState(0);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const searchModel: GoodsPickupOrderSearchModel = {
        page: 1,
        pageSize: 50,
        searchName: search.trim() || undefined,
        status: filterStatus === 'all' ? undefined : filterStatus,
      };

      const response = await goodsPickupOrderService.getOrders(searchModel);
      const data = Array.isArray(response.data) ? response.data : [];
      setOrders(data);
      setTotalOrders(response.pagination?.total || data.length);

      setPendingOrders(data.filter(o => pickupOrderStatus(o) === 'pending').length);
      setDeliveredOrders(data.filter(o => pickupOrderStatus(o) === 'delivered').length);
      setCanceledOrders(data.filter(o => pickupOrderStatus(o) === 'canceled').length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar ordens de retirada');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [filterStatus]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadOrders();
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const handleDelete = async () => {
    if (!selectedOrder) return;
    try {
      setLoading(true);
      await goodsPickupOrderService.deleteOrder(selectedOrder.goods_pickup_orders_id);
      await loadOrders();
      setShowDeleteDialog(false);
      setSelectedOrder(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir ordem de retirada');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDelivered = async (order: GoodsPickupOrder) => {
    try {
      await goodsPickupOrderService.markAsDelivered(order.goods_pickup_orders_id);
      await loadOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao marcar como entregue');
    }
  };

  const handleCancel = async (reason: string) => {
    if (!cancelTarget) return;
    await goodsPickupOrderService.cancelOrder(cancelTarget.goods_pickup_orders_id, reason);
    setCancelTarget(null);
    await loadOrders();
  };

  const formatDate = (dateStr?: string) => formatDateBR(dateStr) || '—';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-h1 mb-2">Ordens de Retirada</h1>
          <p className="text-slate-600">
            Gerenciar solicitações de retirada de mercadorias e produtos
          </p>
        </div>
        <PermissionGuard permission="goods_pickup_orders.create">
          <Button
            icon={<Plus size={20} />}
            onClick={() => navigate('/goods-pickup-orders/new')}
          >
            Nova Ordem de Retirada
          </Button>
        </PermissionGuard>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Truck className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalOrders}</p>
                <p className="text-small text-slate-500">Total de ordens</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{pendingOrders}</p>
                <p className="text-small text-slate-500">Pendentes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{deliveredOrders}</p>
                <p className="text-small text-slate-500">Entregues</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-200 rounded-lg">
                <Ban className="text-slate-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{canceledOrders}</p>
                <p className="text-small text-slate-500">Canceladas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por descrição, fornecedor ou endereço..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                startIcon={<Search size={20} />}
              />
            </div>
            <div className="flex gap-2">
              <select
                className="input-field text-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
              >
                <option value="all">Todos</option>
                <option value="pending">Pendentes</option>
                <option value="delivered">Entregues</option>
                <option value="canceled">Canceladas</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader title="Lista de Ordens de Retirada" />
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-500">Carregando ordens de retirada...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <Truck className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={loadOrders} variant="outline" size="sm">
                Tentar novamente
              </Button>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-8">
              <Truck className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">
                {search ? 'Nenhuma ordem encontrada' : 'Nenhuma ordem de retirada cadastrada'}
              </p>
              <PermissionGuard permission="goods_pickup_orders.create">
                <Button onClick={() => navigate('/goods-pickup-orders/new')}>
                  Criar primeira ordem de retirada
                </Button>
              </PermissionGuard>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">#</th>
                  <th className="text-left">Descrição</th>
                  <th className="text-left">Endereço de Retirada</th>
                  <th className="text-left">Prazo</th>
                  <th className="text-center">Status</th>
                  <th className="text-left">Solicitante</th>
                  <th className="text-center">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {orders.map((order) => (
                  <TableRow key={order.goods_pickup_orders_id}>
                    <TableCell>
                      <span className="font-mono text-sm text-slate-600">
                        #{order.goods_pickup_orders_id}
                      </span>
                    </TableCell>

                    <TableCell>
                      <div className="max-w-xs">
                        <p className="font-medium text-slate-900 truncate">
                          {order.description}
                        </p>
                        {order.provider_name && (
                          <p className="text-xs text-slate-500">
                            Fornecedor: {order.provider_name}
                          </p>
                        )}
                        {order.purchase_order_id && (
                          <p className="text-xs text-slate-500">
                            PC #{order.purchase_order_id}
                          </p>
                        )}
                        {order.order_id && (
                          <p className="text-xs text-slate-500">
                            PV #{order.order_id}
                          </p>
                        )}
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="flex items-start gap-1">
                        <MapPin size={12} className="mt-0.5 text-slate-400 shrink-0" />
                        <div className="text-sm">
                          <p className="text-slate-900">
                            {order.ship_addr_street}, {order.ship_addr_num}
                          </p>
                          <p className="text-xs text-slate-500">
                            {order.ship_addr_city}/{order.ship_addr_state} — CEP {order.ship_addr_zip}
                          </p>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar size={12} className="text-slate-400" />
                        <span className="text-sm">{formatDate(order.pickup_deadline_date)}</span>
                      </div>
                    </TableCell>

                    <TableCell className="text-center">
                      <Badge variant={pickupOrderStatusBadge(pickupOrderStatus(order))}>
                        {PICKUP_ORDER_STATUS_LABELS[pickupOrderStatus(order)]}
                      </Badge>
                    </TableCell>

                    <TableCell>
                      <span className="text-sm text-slate-600">
                        {order.requestor_email || order.created_by || '—'}
                      </span>
                    </TableCell>

                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <PermissionGuard permission="goods_pickup_orders.read">
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Eye size={16} />}
                            onClick={() => navigate(`/goods-pickup-orders/${order.goods_pickup_orders_id}`)}
                            className="px-2"
                          />
                        </PermissionGuard>

                        {pickupOrderStatus(order) === 'pending' && (
                          <>
                            <PermissionGuard permission="goods_pickup_orders.write">
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Edit size={16} />}
                                onClick={() => navigate(`/goods-pickup-orders/${order.goods_pickup_orders_id}/edit`)}
                                className="px-2"
                              />
                            </PermissionGuard>

                            <PermissionGuard permission="goods_pickup_orders.write">
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<CheckCircle size={16} />}
                                onClick={() => handleMarkDelivered(order)}
                                className="px-2 text-green-600 hover:text-green-700"
                                title="Marcar como entregue"
                              />
                            </PermissionGuard>

                            <PermissionGuard permission="goods_pickup_orders.write">
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<XCircle size={16} />}
                                onClick={() => setCancelTarget(order)}
                                className="px-2 text-amber-600 hover:text-amber-700"
                                title="Cancelar ordem"
                              />
                            </PermissionGuard>

                            <PermissionGuard permission="goods_pickup_orders.delete">
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Trash2 size={16} />}
                                onClick={() => {
                                  setSelectedOrder(order);
                                  setShowDeleteDialog(true);
                                }}
                                className="px-2 text-red-500 hover:text-red-700"
                              />
                            </PermissionGuard>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedOrder(null);
        }}
        onConfirm={handleDelete}
        title="Excluir Ordem de Retirada"
        message={`Deseja realmente excluir a ordem #${selectedOrder?.goods_pickup_orders_id}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        severity="danger"
        loading={loading}
      />

      <CancelOrderDialog
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        title="Cancelar Ordem de Retirada"
        subject={`a ordem de retirada #${cancelTarget?.goods_pickup_orders_id ?? ''}`}
      />
    </div>
  );
};
